import React, { useEffect, useRef } from "react"
import { IoMdCheckmark } from "react-icons/io"
import { RxCross2 } from "react-icons/rx"
import { LuAlignLeft, LuArrowLeft, LuColumns2, LuHistory, LuList, LuPencil, LuPlus } from "react-icons/lu"
import styles from "asset/styles/quotation/quotationPreviewV2.module.scss"
import { isHtmlEmpty } from "component/richTextEditor/RichTextEditor"
import { toTitleCase } from "util/textcase"
import type { QuotationPreviewHotelRow } from "pages/quotation/quotationPreviewUtil"
import type {
	IQuotationPreviewV2CitiesView,
	IQuotationPreviewV2DetailItem,
	IQuotationPreviewV2ItineraryRow,
	IQuotationPreviewV2PaxCounts,
	IQuotationPreviewV2Tab,
	IQuotationPreviewV2VersionChip,
	QuotationPreviewV2ItineraryMode,
	QuotationPreviewV2TabKey,
} from "./quotationPreviewV2Types"
import { formatSignedAmount, formatUsd } from "./quotationPreviewV2Util"

const DetailItem: React.FC<{ label: React.ReactNode, children: React.ReactNode, className?: string }> = ({ label, children, className }) => (
	<div className={[styles.detailItem, className].filter(Boolean).join(" ")}>
		<div className={styles.detailLabel}>{label}</div>
		<div className={styles.detailValue}>{children}</div>
	</div>
)

type IOverviewProps = {
	staCode: string
	statusSlot: React.ReactNode
	actionsSlot: React.ReactNode
	contactDetails: IQuotationPreviewV2DetailItem[]
	travelDatesText: string
	pax: IQuotationPreviewV2PaxCounts
	cities: IQuotationPreviewV2CitiesView
	totalPriceText: string
	currentVersionNo: number
	onBack: () => void
	onEdit: () => void
}

export const QuotationPreviewV2Overview: React.FC<IOverviewProps> = (props) => {

	const { staCode, statusSlot, actionsSlot, contactDetails, travelDatesText, pax, cities, totalPriceText, currentVersionNo, onBack, onEdit } = props
	const paxGroups: [string, number][] = [
		["A", pax.adults],
		["EB", pax.extraBed],
		["CWB", pax.childWithBed],
		["CWOB", pax.childWithoutBed],
	]

	return (
		<section className={styles.overview}>
			<div className={styles.titleRow}>
				<div className={styles.identity}>
					<button type="button" className={styles.backButton} onClick={onBack} aria-label="Back to quotations">
						<LuArrowLeft size={16} />
					</button>
					<h1 className={styles.staCode}>{staCode || "-"}</h1>
					{statusSlot}
				</div>
				<div className={styles.titleActions}>
					<button type="button" className={styles.primaryButton} onClick={onEdit}>
						<LuPencil size={16} />
						Edit
					</button>
					{actionsSlot}
				</div>
			</div>

			<div className={styles.contactRow}>
				{contactDetails.map((item) => (
					<DetailItem key={item.label} label={item.label}>{item.value}</DetailItem>
				))}
			</div>

			<div className={styles.divider} />

			<div className={styles.travelRow}>
				<DetailItem label="Travel dates">{travelDatesText}</DetailItem>
				<DetailItem label={`Total Pax · ${pax.total}`}>
					<span className={styles.paxGroups}>
						{paxGroups.map(([label, count]) => (
							<span key={label} className={styles.paxGroup}>{label}<span>{count}</span></span>
						))}
					</span>
				</DetailItem>
				<DetailItem label="Cities">
					{cities.all.length === 0 ? "-" : (
						<span className={styles.cities}>
							{cities.visible.map(toTitleCase).join(" · ")}
							{cities.hidden.length > 0 && (
								<span className={styles.citiesMore} tabIndex={0}>
									+{cities.hidden.length}
									<span className={styles.citiesPopover} role="tooltip">
										{cities.all.map((city) => (
											<span key={city}>{toTitleCase(city)}</span>
										))}
									</span>
								</span>
							)}
						</span>
					)}
				</DetailItem>
				<DetailItem label={`Total price · V${currentVersionNo}`} className={styles.totalPrice}>
					{totalPriceText}
				</DetailItem>
			</div>
		</section>
	)
}

type IVersionBarProps = {
	currentVersionNo: number
	chips: IQuotationPreviewV2VersionChip[]
	flagShowCompare: boolean
	flagShowNewVersion: boolean
	flagBusy: boolean
	onSelectVersion: (quotationId: string) => void
	onNewVersion: () => void
}

export const QuotationPreviewV2VersionBar: React.FC<IVersionBarProps> = (props) => {

	const { currentVersionNo, chips, flagShowCompare, flagShowNewVersion, flagBusy, onSelectVersion, onNewVersion } = props

	const chipScrollRef = useRef<HTMLDivElement>(null)
	const activeChipRef = useRef<HTMLButtonElement>(null)

	// With many versions the chips scroll sideways; bring the viewed version into view.
	useEffect(() => {
		const container = chipScrollRef.current
		const activeChip = activeChipRef.current
		if (!container || !activeChip) return
		const chipStart = activeChip.offsetLeft
		const chipEnd = chipStart + activeChip.offsetWidth
		if (chipStart < container.scrollLeft || chipEnd > container.scrollLeft + container.clientWidth) {
			container.scrollLeft = chipStart - (container.clientWidth - activeChip.offsetWidth) / 2
		}
	}, [chips])

	return (
		<section className={styles.versionBar}>
			<div className={styles.versionChips}>
				<span className={styles.viewingLabel}>
					<LuHistory size={16} />
					Viewing V{currentVersionNo}
				</span>
				<div ref={chipScrollRef} className={styles.versionChipScroll}>
					{chips.map((chip) => (
						<button
							key={chip.quotationId || chip.versionNo}
							ref={chip.flagActive ? activeChipRef : undefined}
							type="button"
							className={[styles.versionChip, chip.flagActive ? styles.versionChipActive : ""].filter(Boolean).join(" ")}
							onClick={() => onSelectVersion(chip.quotationId)}
							aria-pressed={chip.flagActive}
						>
							<span>V{chip.versionNo}{chip.priceUsd != null && ` · ${formatUsd(chip.priceUsd)}`}</span>
							{chip.diffUsd != null && chip.diffUsd !== 0 && (
								<span className={chip.diffUsd > 0 ? styles.diffIncrease : styles.diffDecrease}>
									{formatSignedAmount(chip.diffUsd)}
								</span>
							)}
							{chip.flagLatest && <span className={styles.latestLabel}>· latest</span>}
						</button>
					))}
				</div>
			</div>
			<div className={styles.versionActions}>
				{flagShowCompare && (
					<button type="button" className={styles.outlineButton} disabled title="Coming soon">
						<LuColumns2 size={16} />
						Compare
					</button>
				)}
				{flagShowNewVersion && (
					<button type="button" className={styles.primaryButton} onClick={onNewVersion} disabled={flagBusy}>
						<LuPlus size={16} />
						New version
					</button>
				)}
			</div>
		</section>
	)
}

type ITabsProps = {
	tabs: IQuotationPreviewV2Tab[]
	activeTab: QuotationPreviewV2TabKey
	onChange: (tab: QuotationPreviewV2TabKey) => void
}

export const QuotationPreviewV2Tabs: React.FC<ITabsProps> = ({ tabs, activeTab, onChange }) => (
	<nav className={styles.tabs} role="tablist">
		{tabs.map((tab) => (
			<button
				key={tab.key}
				type="button"
				role="tab"
				aria-selected={tab.key === activeTab}
				className={[styles.tab, tab.key === activeTab ? styles.tabActive : ""].filter(Boolean).join(" ")}
				onClick={() => onChange(tab.key)}
				disabled={!tab.flagEnabled}
				title={tab.flagEnabled ? undefined : "Coming soon"}
			>
				{tab.label}
				{tab.badge != null && <span className={styles.tabBadge}>{tab.badge}</span>}
			</button>
		))}
	</nav>
)

type IItineraryProps = {
	rows: IQuotationPreviewV2ItineraryRow[]
	versionBadge: string
	mode: QuotationPreviewV2ItineraryMode
	onModeChange: (mode: QuotationPreviewV2ItineraryMode) => void
}

const ITINERARY_MODES: { key: QuotationPreviewV2ItineraryMode, label: string, Icon: typeof LuList }[] = [
	{ key: "draft", label: "Draft", Icon: LuList },
	{ key: "detailed", label: "Detailed", Icon: LuAlignLeft },
]

export const SectionHeading: React.FC<{ title: string, versionBadge: string }> = ({ title, versionBadge }) => (
	<div className={styles.sectionHeading}>
		<h2 className={styles.sectionTitle}>{title}</h2>
		<span className={styles.versionBadge}>{versionBadge}</span>
	</div>
)

export const QuotationPreviewV2Itinerary: React.FC<IItineraryProps> = ({ rows, versionBadge, mode, onModeChange }) => (
	<section>
		<div className={styles.sectionToolbar}>
			<SectionHeading title="Itinerary" versionBadge={versionBadge} />
			<div className={styles.modeToggle} role="group" aria-label="Itinerary view">
				{ITINERARY_MODES.map(({ key, label, Icon }) => (
					<button
						key={key}
						type="button"
						className={[styles.modeButton, mode === key ? styles.modeButtonActive : ""].filter(Boolean).join(" ")}
						onClick={() => onModeChange(key)}
						aria-pressed={mode === key}
					>
						<Icon size={14} />
						{label}
					</button>
				))}
			</div>
		</div>

		<div className={styles.tableCard}>
			<table className={styles.table}>
				<thead>
					<tr>
						<th>Day</th>
						<th>Date</th>
						<th>Overnight</th>
						<th className={styles.itineraryHeader}>Itinerary</th>
						<th>Meals</th>
						<th>Guide</th>
					</tr>
				</thead>
				<tbody>
					{rows.length === 0 ? (
						<tr><td colSpan={6} className={styles.emptyRow}>No itinerary added</td></tr>
					) : rows.map((row, rowIndex) => (
						<tr key={`it-${rowIndex}`}>
							<td>{toTitleCase(row.dayLabel)}</td>
							<td>{row.date}</td>
							<td><span className={styles.cityBadge}>{toTitleCase(row.overnight)}</span></td>
							<td className={styles.itineraryCell}>
								{row.tripBlocks.length === 0 ? (
									<div className={styles.programTitle}>Free Day</div>
								) : row.tripBlocks.map((block, blockIndex) => (
									<div key={`trip-${blockIndex}`} className={styles.tripBlock}>
										{block.name && <div className={styles.programTitle}>{toTitleCase(block.name)}</div>}
										{mode === "detailed" && !isHtmlEmpty(block.itineraryHtml) && (
											<div
												className={styles.itineraryHtml}
												// Itinerary is admin-authored rich text stored as HTML.
												dangerouslySetInnerHTML={{ __html: block.itineraryHtml }}
											/>
										)}
									</div>
								))}
							</td>
							<td>{toTitleCase(row.meals)}</td>
							<td>{toTitleCase(row.guide)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	</section>
)

type IHotelsProps = {
	rows: QuotationPreviewHotelRow[]
	versionBadge: string
}

export const QuotationPreviewV2Hotels: React.FC<IHotelsProps> = ({ rows, versionBadge }) => (
	<section>
		<div className={styles.sectionToolbar}>
			<SectionHeading title="Hotels" versionBadge={versionBadge} />
		</div>

		<div className={styles.tableCard}>
			<table className={`${styles.table} ${styles.fixedRowsTable}`}>
				<thead>
					<tr>
						<th>City</th>
						<th>Hotel Name</th>
						<th>Room Category</th>
						<th>Check In</th>
						<th>Check Out</th>
					</tr>
				</thead>
				<tbody>
					{rows.length === 0 ? (
						<tr><td colSpan={5} className={styles.emptyRow}>Hotels to be confirmed</td></tr>
					) : rows.map((row, rowIndex) => (
						<tr key={`ht-${rowIndex}`}>
							<td>{toTitleCase(row.city)}</td>
							<td>{toTitleCase(row.hotel_name)}</td>
							<td>{toTitleCase(row.room)}</td>
							<td>{toTitleCase(row.checkIn)}</td>
							<td>{toTitleCase(row.checkOut)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	</section>
)

type IInclusionsExclusionsProps = {
	inclusionLines: string[]
	exclusionLines: string[]
	versionBadge: string
}

const LineListCard: React.FC<{ title: string, lines: string[], icon: React.ReactNode, className: string }> = ({ title, lines, icon, className }) => (
	<div className={[styles.lineCard, className].join(" ")}>
		<h3 className={styles.lineCardHeader}>{title}</h3>
		<ul className={styles.lineList}>
			{lines.length === 0 ? (
				<li className={styles.emptyRow}>-</li>
			) : lines.map((line, lineIndex) => (
				<li key={`${title}-${lineIndex}`}>
					{icon}
					<span>{toTitleCase(line)}</span>
				</li>
			))}
		</ul>
	</div>
)

export const QuotationPreviewV2InclusionsExclusions: React.FC<IInclusionsExclusionsProps> = ({ inclusionLines, exclusionLines, versionBadge }) => (
	<section>
		<div className={styles.sectionToolbar}>
			<SectionHeading title="Inclusions and Exclusions" versionBadge={versionBadge} />
		</div>

		<div className={styles.lineGrid}>
			<LineListCard title="Inclusions" lines={inclusionLines} icon={<IoMdCheckmark />} className={styles.inclusions} />
			<LineListCard title="Exclusions" lines={exclusionLines} icon={<RxCross2 />} className={styles.exclusions} />
		</div>
	</section>
)
